import React, { useState, useEffect, useMemo } from "react";
import { Modal, Form } from "react-bootstrap";
import Select from 'react-select';
import { dataUser } from "../../services/dashboard/GetUsers";
import { updateUser } from "../../services/dashboard/UpdUserRequest "; // Importa el servicio
import "../../styles/dash/CreateOrEditChatBot.css";
import LoadingSpinner from "../utility/LoadingSpinner";

interface Props {
    user: dataUser | null;
    roles: any[];
    onClose: () => void;
    onSuccess: (updatedUser: dataUser) => void;
}

// Tipo para las opciones de react-select
interface SelectOption {
    value: number;
    label: string;
}

const EditUserModal: React.FC<Props> = ({ user, roles, onClose, onSuccess }) => {
    const [username, setUsername] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [status, setStatus] = useState(true);
    const [selectedRoles, setSelectedRoles] = useState<SelectOption[]>([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [animateOut, setAnimateOut] = useState(false);

    // Convertir roles a opciones para react-select
    const roleOptions = useMemo(() => {
        return roles.map(role => ({
            value: role.id,
            label: role.roleName
        }));
    }, [roles]);

    const handleClose = () => {
        setAnimateOut(true);
        setTimeout(() => {
            onClose();
            setAnimateOut(false);
        }, 300);
    };

    const handleSubmit = async () => {
        setLoading(true);
        setErrorMessage("");

        // Validaciones
        if (!username.trim()) {
            setErrorMessage("El nombre de usuario es requerido");
            setLoading(false);
            return;
        }

        if (!email.trim()) {
            setErrorMessage("El email es requerido");
            setLoading(false);
            return;
        }

        if (password && password.length < 6) {
            setErrorMessage("La contraseña debe tener al menos 6 caracteres");
            setLoading(false);
            return;
        }

        try {
            // Preparar datos para la API
            const userData = {
                IdUser: user?.id || 0,
                UserName: username,
                Email: email,
                Roles: selectedRoles.map(role => role.value),
                ...(password && { NewPassword: password }) // Solo incluye si hay password
            };

            // Llamar al servicio de actualización
            const result = await updateUser(userData);

            // Si el backend responde con éxito
            if (result.status === 200) {
                // Construir el usuario actualizado para actualizar la UI
                const updatedUser: dataUser = {
                    id: user?.id || 0,
                    user: username,
                    email: email,
                    status: status ? 1 : 0,
                    roles: selectedRoles.map(role => ({
                        idRole: role.value,
                        nameRole: role.label
                    }))
                };

                onSuccess(updatedUser);
                handleClose();
            } else {
                // Manejar errores del backend
                setErrorMessage(result.message || "Error al actualizar el usuario");
            }
        } catch (error: any) {
            // Manejar errores de la petición
            setErrorMessage(error.message || "Error al actualizar el usuario");
            console.error("Error al guardar:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user) {
            setUsername(user.user);
            setEmail(user.email);
            setStatus(user.status === 1);
            
            // Convertir roles del usuario a formato para react-select
            const userRoles = user.roles.map(role => ({
                value: role.idRole,
                label: role.nameRole
            }));
            setSelectedRoles(userRoles);
        } else {
            setUsername("");
            setEmail("");
            setStatus(true);
            setSelectedRoles([]);
        }
        setPassword("");
    }, [user]);

    return (
        <Modal
            show={!animateOut}
            onHide={handleClose}
            centered
            size="lg"
            backdrop="static"
            contentClassName={`chatbot-form-modal ${animateOut ? 'modal-exit' : 'modal-enter'}`}
        >
            <Modal.Header closeButton closeVariant="white" className="border-0" />

            <Modal.Body className="chatbot-form-content">
                <h5 className="form-title text-center mb-4">
                    {user ? "Editar Usuario" : "Crear Usuario"}
                </h5>

                <Form>
                    {loading && <LoadingSpinner />}
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Nombre de usuario</label>
                            <input
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder="Ej: juanperez"
                            />
                        </div>

                        <div className="form-group">
                            <label>Email</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Ej: usuario@ejemplo.com"
                            />
                        </div>

                        <div className="form-group">
                            <label>Contraseña</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Dejar vacío para no cambiar"
                            />
                            <div className="password-hint">
                                (Solo complete si desea cambiar la contraseña)
                            </div>
                        </div>

                        <div className="form-group full-width">
                            <label>Roles</label>
                            <Select
                                isMulti
                                options={roleOptions}
                                value={selectedRoles}
                                onChange={(newValue) => setSelectedRoles([...newValue])}
                                placeholder="Seleccione roles..."
                                className="basic-multi-select"
                                classNamePrefix="select"
                                menuPortalTarget={document.body} 
                                menuPosition="fixed" 
                                styles={{
                                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                                    control: (base) => ({
                                        ...base,
                                        border: '1px solid #444444',
                                        borderRadius: '4px',
                                        minHeight: '38px',
                                        backgroundColor: '#2a2a2a',
                                        color: '#fff'
                                    }),
                                    menu: (base) => ({
                                        ...base,
                                        backgroundColor: '#2a2a2a',
                                        color: '#fff'
                                    }),
                                    multiValue: (base) => ({
                                        ...base,
                                        backgroundColor: '#2a2a2a',
                                        color: '#fff'
                                    }),
                                    multiValueLabel: (base) => ({
                                        ...base,
                                        color: '#fff'
                                    }),
                                    option: (base, { isFocused }) => ({
                                        ...base,
                                        backgroundColor: isFocused ? '#2a2a2a' : '#1d1c1cff',
                                        color: '#fff'
                                    }),
                                    singleValue: (base) => ({
                                        ...base,
                                        color: '#fff'
                                    })
                                }}
                            />
                        </div>

                        <div className="form-group full-width">
                            {errorMessage && <div className="error-message">{errorMessage}</div>}

                            <button 
                                type="button" 
                                className="save-button" 
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                {loading ? "GUARDANDO..." : "GUARDAR CAMBIOS"}
                            </button>
                        </div>

                        <div className="form-group full-width text-center">
                            <button type="button" className="cancel-button" onClick={handleClose}>
                                Cerrar
                            </button>
                        </div>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default EditUserModal;